import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCountries } from "@/hooks/useCountries";
import {
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { RiskBadge } from "./RiskBadge";
import { formatFileSize } from "@/shared/core";
import type { DocumentReviewCardProps } from "../types/component.types";

export const DocumentReviewCard: React.FC<DocumentReviewCardProps> = ({
  review,
  basePath = "",
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getCountryName } = useCountries();

  const locale = i18n.language === "es" ? es : enUS;

  const handleClick = () => {
    navigate(`${basePath}/document-review/${review.id}`);
  };

  const getStatusIcon = () => {
    switch (review.analysis_status) {
      case "completed":
        return <CheckCircleIcon className="h-5 w-5 text-atlantis-600" />;
      case "failed":
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case "processing":
        return (
          <div className="h-5 w-5 border-2 border-tiber-600 border-t-transparent rounded-full animate-spin" />
        );
      default:
        return <ClockIcon className="h-5 w-5 text-titan" />;
    }
  };

  const getStatusText = () => {
    return t(`documentReview.status.${review.analysis_status}`);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-lg border border-tiber-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="shrink-0">
          <div className="h-12 w-12 rounded-lg bg-tiber-50 flex items-center justify-center">
            <DocumentTextIcon className="h-6 w-6 text-tiber-600" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-juridico truncate">
                {review.document_name}
              </h3>
              <p className="text-xs text-titan mt-0.5">
                {getCountryName(review.country)} • {review.document_type}
                {review.file_size && ` • ${formatFileSize(review.file_size)}`}
              </p>
            </div>

            {review.analysis_status === "completed" &&
              review.analysis_result?.overall_risk && (
                <RiskBadge
                  risk={review.analysis_result.overall_risk}
                  size="sm"
                />
              )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 mb-2">
            {getStatusIcon()}
            <span className="text-sm text-tiber-700">{getStatusText()}</span>
          </div>

          {/* Summary (if completed) */}
          {review.analysis_status === "completed" &&
            review.analysis_result?.summary && (
              <p className="text-sm text-tiber-700 line-clamp-2 mb-2">
                {review.analysis_result.summary}
              </p>
            )}

          {/* Key metrics (if completed) */}
          {review.analysis_status === "completed" && review.analysis_result && (
            <div className="flex items-center gap-4 text-xs text-titan">
              <span>
                {review.analysis_result.risks.length}{" "}
                {t("documentReview.sections.risks")}
              </span>
              <span>
                {review.analysis_result.omissions.length}{" "}
                {t("documentReview.sections.omissions")}
              </span>
              <span>
                {review.analysis_result.sensitive_clauses.length}{" "}
                {t("documentReview.sections.clauses")}
              </span>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-tiber-100">
            <span className="text-xs text-titan">
              {formatDistanceToNow(new Date(review.created_at), {
                addSuffix: true,
                locale,
              })}
            </span>
            {review.analyzed_at && (
              <span className="text-xs text-titan">
                {t("documentReview.analyzedAt")}:{" "}
                {formatDistanceToNow(new Date(review.analyzed_at), {
                  addSuffix: true,
                  locale,
                })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
